import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { Card } from '@/Domain/components/Shared/Card/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { UpgradeBadge } from '@/Domain/components/Shared/UpgradeBadge/UpgradeBadge';
import {
  AiModelData,
  isModelLocked,
  MODEL_DEFAULT_VALUE,
  MODEL_OPTIONS,
} from '@/UI/Identity/AiModel/aiModel.constants';
import { IdentityFieldRow } from '@/UI/Identity/components/IdentityFieldRow';

export const IdentityModelTab = ({ model }: { model?: AiModelData }) => {
  const { t } = useTranslation('identity');
  const queryClient = useQueryClient();

  const mutation = useMutation<unknown, unknown, { advanced_model: string | null }>({
    mutationKey: [QueryKey.aiModel.patch],
    onMutate: (vars) => {
      queryClient.setQueryData<AiModelData>([QueryKey.aiModel.get], (prev) =>
        prev ? { ...prev, advanced_model: vars.advanced_model } : prev,
      );
    },
    // `usage` carries the per-message credit rate, which the new model changes; with the
    // app-wide `staleTime: Infinity` it would otherwise quote the old rate all session.
    onSettled: () =>
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.aiModel.get] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.subscription.usage] }),
      ]),
  });

  if (!model) return <Card className="px-6" />;

  const locked = (o: (typeof MODEL_OPTIONS)[number]) => isModelLocked(o, model.unlocked_models);
  const anyModelLocked = MODEL_OPTIONS.some(locked);
  const currentStored = model.advanced_model ?? MODEL_DEFAULT_VALUE;
  const currentOpt = MODEL_OPTIONS.find((o) => o.storedValue === currentStored) ?? MODEL_OPTIONS[0];
  // A saved-but-now-locked model (e.g. after a downgrade) falls back to the default in the trigger.
  const modelValue = locked(currentOpt) ? MODEL_OPTIONS[0].storedValue : currentOpt.storedValue;
  const currentModelOpt =
    MODEL_OPTIONS.find((o) => o.storedValue === modelValue) ?? MODEL_OPTIONS[0];

  const onModelChange = (stored: string) => {
    const opt = MODEL_OPTIONS.find((o) => o.storedValue === stored);
    if (!opt || locked(opt)) return;
    mutation.mutate({ advanced_model: stored === MODEL_DEFAULT_VALUE ? null : opt.value });
  };

  return (
    <Card className="px-6">
      <IdentityFieldRow
        label={t('model.title')}
        description={t('model.description')}
        labelAddon={anyModelLocked ? <UpgradeBadge /> : undefined}
      >
        <Select value={modelValue} onValueChange={onModelChange}>
          <SelectTrigger className="w-full" data-testid="identity-model-select">
            <SelectValue>{t(currentModelOpt.labelKey)}</SelectValue>
          </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)]">
            {MODEL_OPTIONS.map((o) => (
              <SelectItem
                key={o.storedValue}
                value={o.storedValue}
                disabled={locked(o)}
                className="py-2.5"
                data-testid={`identity-model-option-${o.storedValue}`}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{t(o.labelKey)}</span>
                    {locked(o) && (
                      <UpgradeBadge
                        className="pointer-events-auto"
                        data-testid={`ai-model-lock-badge-${o.storedValue}`}
                      />
                    )}
                  </div>
                  <div className="text-pretty text-xs font-normal text-muted-foreground">
                    {t(o.descriptionKey)}
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </IdentityFieldRow>
    </Card>
  );
};
